/**
 * MOTOR DE SINCRONIZACIÓN (FASE 5.3)
 *
 * Único punto por el que una mutación local llega a `POST /api/sync`.
 * Conecta la cola de FASE 5.2 (`offline/mutaciones.ts`/`offline/db.ts`)
 * con el servidor — nunca al revés: ninguna pantalla llama a `/api/sync`
 * directamente, todas pasan por `sincronizarPendientes()`.
 *
 * MUTEX EN MEMORIA (§17)
 *
 * `vueloEnCurso` es la única fuente de "¿hay un POST en marcha ahora
 * mismo?". Si el evento `online`, el botón manual y el "foreground" de la
 * pestaña disparan sincronización casi a la vez, los tres reciben la
 * MISMA promesa — nunca se lanza un segundo `POST /api/sync` mientras el
 * primero sigue en vuelo. El servidor sigue siendo la última defensa
 * (idempotencia real, FASE 4.2-A) por si dos PESTAÑAS distintas del mismo
 * usuario sincronizan a la vez: eso este mutex, que vive en la memoria de
 * UNA pestaña, no puede evitarlo.
 *
 * FALLO DE RED (§18)
 *
 * Si el propio `fetch` no llega a responder, NINGUNA mutación cambia de
 * estado: se quedan exactamente como estaban (PENDIENTE o
 * ERROR_REINTENTABLE), sin perder `idOperacionCliente`, orden ni payload.
 * Sólo una respuesta real del servidor —aunque sea con resultados
 * negativos— mueve el estado de una mutación.
 *
 * IDENTIDAD (F14.2)
 *
 * Nunca se envía nada si el perfil desbloqueado no es el del usuario de la
 * sesión en línea (`identidad-sync.ts`): se comprueba aquí, en el nivel común,
 * para todo disparo (manual, reconexión, primer plano, desbloqueo, reintento).
 */
import { useEffect, useState } from 'react';

import { api, FalloApi } from '../api.ts';
import {
  listarMutacionesSincronizables,
  resolverMutacionTrasSincronizacion,
  type Mutacion,
  type ResultadoMntMutacion,
} from './db.ts';
import { esperaReintento, MAX_REINTENTOS_AUTOMATICOS, sincronizableAutomaticamente } from './estados-sync.ts';
import { definicionDe, type ResultadoAplicadoLote, type ResumenParcial } from './operaciones.ts';
//  Registro de los tipos existentes (Limpieza y Calidad). Un dominio nuevo (F14.3, F14.4) añade aquí su módulo.
import './operaciones-limpieza.ts';
import './operaciones-mantenimiento.ts';
//  Compatibilidad: estas dos funciones vivían aquí antes de F14.1.
export { resolverDependenciaEvidenciaNc, rutaEvidencia } from './operaciones-limpieza.ts';
import { prepararPaqueteOffline } from './paquete.ts';
import { cabeceraPerfilOffline, comprobarIdentidadSync, identidadSigueVigente, type MotivoSinIdentidad } from './identidad-sync.ts';
import { ErrorPerfilBloqueado, hayPerfilDesbloqueado } from './perfil.ts';

export interface EventoSyncHttp {
  readonly idEvento: string;
  readonly idLinea: string;
  readonly estadoAnterior: string;
  readonly estadoNuevo: string;
  readonly fechaEfectiva: string;
  readonly fechaRegistro: string;
  readonly otEstado: string;
}

/** Réplica de `NcSync` (backend `sync/tipos.ts`) — lo mínimo para reconciliar la NC local sin una segunda consulta (FASE 5.4 §7). */
export interface NcSyncHttp {
  readonly idNc: string;
  readonly codigo: string;
  readonly idOt: string;
  readonly estado: string;
  readonly ocurridoEn: string;
  readonly registradoEn: string;
}

export type ResultadoItemSyncHttp =
  | { readonly idOperacionCliente: string; readonly resultado: 'APLICADA' | 'YA_APLICADA'; readonly evento: EventoSyncHttp; readonly revisionServidorActual: string }
  | { readonly idOperacionCliente: string; readonly resultado: 'APLICADA' | 'YA_APLICADA'; readonly nc: NcSyncHttp; readonly revisionServidorActual: string }
  //  F14.3 — mantenimiento (`MntSync`): lo reconcilia `operaciones-mantenimiento.ts`.
  | { readonly idOperacionCliente: string; readonly resultado: 'APLICADA' | 'YA_APLICADA'; readonly mnt: ResultadoMntMutacion; readonly revisionServidorActual: string }
  | {
      readonly idOperacionCliente: string;
      readonly resultado: 'CONFLICTO';
      readonly codigo: string;
      readonly motivo: string;
      readonly revisionBaseConocida: string | null;
      readonly revisionServidorActual: string | null;
      readonly estadoServidorActual: { readonly estado: string };
    }
  | { readonly idOperacionCliente: string; readonly resultado: 'RECHAZADA'; readonly codigo: string; readonly mensaje: string }
  | { readonly idOperacionCliente: string; readonly resultado: 'ERROR_REINTENTABLE'; readonly codigo: string; readonly mensaje: string }
  | { readonly idOperacionCliente: string; readonly resultado: 'OMITIDA'; readonly codigo: string; readonly motivo: string };

interface RespuestaSyncHttp {
  readonly resultados: readonly ResultadoItemSyncHttp[];
}

export interface ResumenSincronizacion {
  readonly intentadas: number;
  readonly aplicadas: number;
  readonly yaAplicadas: number;
  readonly conConflicto: number;
  readonly rechazadas: number;
  readonly errorTemporal: number;
  readonly omitidas: number;
}

// ------------------------------------------------------ estado global (UI)

export type EstadoGlobalSync = 'INACTIVO' | 'SINCRONIZANDO';

type Escucha = (estado: EstadoGlobalSync) => void;
const escuchas = new Set<Escucha>();
let estadoActual: EstadoGlobalSync = 'INACTIVO';

function fijarEstadoGlobal(e: EstadoGlobalSync): void {
  estadoActual = e;
  for (const f of escuchas) f(e);
}

/** Para que cualquier pantalla pinte "Sincronizando…" sin tener que orquestar el envío ella misma. */
export function useEstadoSync(): EstadoGlobalSync {
  const [estado, setEstado] = useState(estadoActual);
  useEffect(() => {
    escuchas.add(setEstado);
    return () => {
      escuchas.delete(setEstado);
    };
  }, []);
  return estado;
}

/**
 * FASE 5.7-B UAT-Q02 — la sesión caducada (401 durante `/api/sync`) y un
 * fallo de red puro se tratan IGUAL en cuanto a los datos (§18: ninguna
 * mutación se toca, en ningún caso) pero NO deben sonar igual al usuario:
 * "no se ha podido contactar con el servidor" es engañoso cuando el
 * problema es que hace falta volver a iniciar sesión, no que no haya red.
 * Bandera mínima, de sólo lectura desde fuera — se fija justo antes de
 * `fijarEstadoGlobal('INACTIVO')` en la ronda que detectó el 401, y se
 * limpia al EMPEZAR la siguiente ronda (nunca a mitad, para que quien lee
 * el resultado de ESTA ronda todavía la vea).
 */
let ultimaRondaTuvoSesionCaducada = false;
export function ultimaSincronizacionFueSesionCaducada(): boolean {
  return ultimaRondaTuvoSesionCaducada;
}

/** F14.2 — por qué la última ronda no envió nada por identidad (p. ej. el perfil es de otro usuario), o `null`. */
let ultimaRondaSinIdentidad: MotivoSinIdentidad | null = null;
export function ultimaSincronizacionSinIdentidad(): MotivoSinIdentidad | null {
  return ultimaRondaSinIdentidad;
}

/**
 * F14.1 — instante de la última ronda que obtuvo respuesta del servidor (para el centro de sincronización).
 * Memoria de la pestaña + `localStorage` por perfil como mera conveniencia (no es un dato operativo: si se pierde,
 * sólo se deja de mostrar la hora).
 */
const CLAVE_ULTIMA = 'neatos.sync.ultima.';
let ultimaSincronizacion: { readonly en: string; readonly resumen: ResumenSincronizacion } | null = null;
export function ultimaSincronizacionDe(idPerfil: string): { readonly en: string; readonly resumen: ResumenSincronizacion } | null {
  if (ultimaSincronizacion !== null) return ultimaSincronizacion;
  try {
    const t = localStorage.getItem(CLAVE_ULTIMA + idPerfil);
    return t === null ? null : (JSON.parse(t) as { en: string; resumen: ResumenSincronizacion });
  } catch {
    return null;
  }
}
function anotarUltimaSincronizacion(idPerfil: string, resumen: ResumenSincronizacion): void {
  ultimaSincronizacion = { en: new Date().toISOString(), resumen };
  try {
    localStorage.setItem(CLAVE_ULTIMA + idPerfil, JSON.stringify(ultimaSincronizacion));
  } catch {
    /* sin almacenamiento: sólo se deja de recordar la hora */
  }
}

/** F14.1 — ¿falló la última ronda por no llegar al servidor? (para espaciar los reintentos automáticos). */
let fallosDeRedSeguidos = 0;

let vueloEnCurso: Promise<ResumenSincronizacion | null> | null = null;
/** Una ronda MANUAL («Sincronizar ahora», «Reintentar») envía también los errores temporales sin esperar su turno. */
let rondaManualPedida = false;
/**
 * Auditoría FASE 5.3, punto 3/4 — si llega una petición de sincronizar
 * MIENTRAS ya hay una ronda en vuelo (por ejemplo: se crea M2 justo
 * cuando el POST de M1 todavía no ha respondido), nunca se lanza un
 * segundo POST en paralelo (eso seguiría violando el mutex) — pero
 * tampoco se puede dejar a M2 esperando a que ocurra algún OTRO evento
 * externo (reconectar, volver a primer plano, pulsar el botón manual):
 * se marca aquí, y `ejecutarRondas` encadena una ronda más en cuanto la
 * actual termina, sin que nadie tenga que volver a pedirlo.
 */
let otraRondaPedida = false;

/**
 * Envía la cola sincronizable (§11: en `ordenLocal`) y reconcilia cada
 * mutación con el resultado que le corresponda. `null` = no había nada
 * que enviar, o el propio `fetch` falló antes de obtener respuesta — en
 * los dos casos la cola queda intacta.
 */
export function sincronizarPendientes(idPerfil: string, opciones: { readonly manual?: boolean } = {}): Promise<ResumenSincronizacion | null> {
  if (opciones.manual === true) rondaManualPedida = true;
  if (vueloEnCurso !== null) {
    otraRondaPedida = true;
    return vueloEnCurso;
  }
  vueloEnCurso = ejecutarRondas(idPerfil);
  return vueloEnCurso;
}

/**
 * Repite `ejecutarSincronizacion` mientras, durante la ronda que acaba de
 * terminar, alguien haya pedido otra (`otraRondaPedida`) — nunca de
 * forma indefinida por sí sola: cada ronda sólo se repite si hubo una
 * petición NUEVA de por medio, así que converge en cuanto no llega nada
 * más mientras se sincroniza.
 */
async function ejecutarRondas(idPerfil: string): Promise<ResumenSincronizacion | null> {
  try {
    let resultado: ResumenSincronizacion | null = null;
    do {
      otraRondaPedida = false;
      const manual = rondaManualPedida;
      rondaManualPedida = false;
      const r = await ejecutarSincronizacion(idPerfil, manual);
      if (r !== null) resultado = r;
    } while (otraRondaPedida);
    return resultado;
  } finally {
    vueloEnCurso = null;
  }
}

/**
 * `EVIDENCIA_SUBIR` NUNCA viaja por aquí (FASE 5.4 §10): `/api/sync` es
 * JSON puro, y una evidencia es un fichero — transporte propio, multipart,
 * ver `sincronizarEvidencias` más abajo. Las líneas y las tres mutaciones
 * NC comparten el mismo lote JSON tal cual (mismo criterio que el backend,
 * `sync/servicio.ts::TIPOS_NC` — aquí no hace falta distinguir el tipo
 * exacto, sólo excluir evidencias).
 */
function esMutacionJson(m: Mutacion): boolean {
  return definicionDe(m.tipo)?.transporte === 'LOTE';
}

/** F14.1 — un tipo que este cliente no conoce (de una versión posterior) nunca se envía a ciegas: espera. */
function esConocida(m: Mutacion): boolean {
  return definicionDe(m.tipo) !== undefined;
}

/**
 * FASE 5.7-A §24 — mismo límite que `backend/src/sync/servicio.ts::
 * MAX_MUTACIONES_POR_LOTE`. Antes de este hardening, un lote JSON con más
 * mutaciones que este límite se enviaba TAL CUAL en un único `POST
 * /api/sync`; el backend lo rechaza ENTERO con 400 `PETICION_INVALIDA`
 * ANTES de tocar ninguna mutación (`validarEnvelope`) — y como ese rechazo
 * llega como una respuesta HTTP real (nunca un fallo de `fetch`), caía en
 * el mismo camino "no se toca nada, se reintenta" que un fallo de red
 * (§18) — pero SIEMPRE con el MISMO lote sobredimensionado, así que la
 * cola quedaba atascada para siempre, indistinguible de "sin conexión"
 * para quien mira la UI. Un lote de más de este tamaño ahora se recorta al
 * límite y `ejecutarRondas` reutiliza `otraRondaPedida` (mismo mecanismo
 * que ya converge tras una mutación nueva creada a mitad de ronda, arriba)
 * para procesar el resto en la siguiente vuelta — nunca un segundo
 * coordinador paralelo.
 */
const MAX_MUTACIONES_POR_LOTE = 100;

async function ejecutarSincronizacion(idPerfil: string, manual: boolean): Promise<ResumenSincronizacion | null> {
  //  IndexedDB es una mejora progresiva, no un requisito (mismo criterio
  //  que `pwa.ts` para el service worker): un contexto sin ella —privado
  //  en algunos navegadores, o un entorno de pruebas que no la simula—
  //  no debe convertir el arranque de sesión en una promesa rechazada sin
  //  gestionar. `activarSincronizacionAutomatica()` se activa para TODA
  //  sesión con capacidad offline, nunca sólo para quien ya abrió
  //  `/offline` — así que tiene que sobrevivir a que esta API no exista.
  if (typeof indexedDB === 'undefined') return null;

  //  FASE 5.6 §17 — "si perfil LOCKED: NO sync". Comprobación explícita en
  //  vez de dejar que `db.ts` lance: un bloqueo ocurrido justo mientras
  //  esta ronda esperaba turno (mutex `vueloEnCurso`) es un caso normal,
  //  nunca un error de programación que deba propagarse como excepción
  //  sin gestionar hacia `activarSincronizacionAutomatica`.
  if (!hayPerfilDesbloqueado(idPerfil)) return null;

  //  F14.2 — perfil offline = usuario de la sesión en línea, por `usuarios.id`. Si no coinciden (otra cuenta en
  //  la sesión, sesión cerrada o token cambiado), no sale ninguna operación y la cola no se toca.
  const identidad = await comprobarIdentidadSync(idPerfil);
  ultimaRondaSinIdentidad = identidad.ok ? null : identidad.motivo;
  if (!identidad.ok) return null;

  //  §Q02 — se limpia al EMPEZAR esta ronda, nunca a mitad: si esta misma
  //  ronda vuelve a toparse con un 401, la marca de nuevo abajo; si no, la
  //  deja en `false` para que un éxito reciente no arrastre un aviso viejo.
  ultimaRondaTuvoSesionCaducada = false;

  //  FASE 5.6 §16 — el bloqueo también puede llegar A MITAD de esta misma
  //  ronda (logout/"cambiar perfil" mientras el POST está en vuelo, o
  //  mientras se reconcilia el resultado): `ErrorPerfilBloqueado`
  //  (`perfil.ts`) es la ÚNICA excepción que este bloque trata como
  //  abandono silencioso de la ronda — igual criterio que un fallo de red
  //  (§18): lo que el servidor ya haya aplicado NUNCA se deshace desde
  //  aquí ("no rollback cliente"); la reconciliación real llega sola en el
  //  siguiente desbloqueo, porque el servidor es idempotente. Cualquier
  //  OTRA excepción se sigue propagando tal cual.
  try {
    //  F14.1 — una ronda automática respeta la espera entre reintentos y el máximo de reintentos automáticos
    //  (`estados-sync.ts`); una manual envía todo lo sincronizable.
    const ahora = Date.now();
    const pendientes = (await listarMutacionesSincronizables(idPerfil))
      .filter(esConocida)
      .filter((m) => manual || sincronizableAutomaticamente(m, ahora));
    if (pendientes.length === 0) return null;

    const pendientesJsonTodas = pendientes.filter(esMutacionJson);
    const pendientesJson = pendientesJsonTodas.slice(0, MAX_MUTACIONES_POR_LOTE);
    if (pendientesJsonTodas.length > MAX_MUTACIONES_POR_LOTE) otraRondaPedida = true;
    const pendientesEvidencia = pendientes.filter((m) => !esMutacionJson(m));

    fijarEstadoGlobal('SINCRONIZANDO');
    try {
      let resumen: ResumenSincronizacion | null = null;

      if (pendientesJson.length > 0) {
        const cuerpo = {
          //  F14.3 — la hora del dispositivo al enviar: el servidor mide con ella el desfase de su reloj y corrige las
          //  horas declaradas de las operaciones (OFF-F142-RELOJ-DISPOSITIVO). Nunca es autoridad por sí sola.
          relojDispositivoEn: new Date().toISOString(),
          mutaciones: pendientesJson.map((m) => ({
            idOperacionCliente: m.idOperacionCliente,
            tipo: m.tipo,
            idEntidad: m.idEntidad,
            idOt: m.idOt,
            revisionBase: m.revisionBase,
            fechaEfectiva: m.fechaEfectiva,
            payload: m.payload,
            dependeDe: m.dependeDe,
          })),
        };

        //  La sesión pudo cambiar mientras se leía la cola: se vuelve a mirar justo antes de enviar.
        if (!identidadSigueVigente(identidad, idPerfil)) return null;
        let respuesta: RespuestaSyncHttp | null = null;
        try {
          respuesta = await api.post<RespuestaSyncHttp>('/api/sync', cuerpo, { headers: cabeceraPerfilOffline(idPerfil) });
        } catch (e) {
          //  §18 — fallo de red completo: no se toca ni una sola mutación.
          //  Las evidencias, más abajo, se intentan igual: son un transporte
          //  independiente, y un fallo de `/api/sync` no dice nada sobre si
          //  `/api/.../evidencias` respondería o no.
          //
          //  §Q02 — un 401 aquí ya disparó `api.ts::peticion` (borra el
          //  token, avisa a `cuandoCaduqueLaSesion`) — esta bandera es
          //  SÓLO para que la UI pueda decir "vuelve a iniciar sesión" en
          //  vez de "sin conexión"; nunca cambia qué se hace con los datos,
          //  que sigue siendo exactamente "no tocar nada" en los dos casos.
          if (e instanceof FalloApi && e.estado === 401) ultimaRondaTuvoSesionCaducada = true;
        }
        //  F14.1 — una respuesta que no es la del contrato (un proxy, una página de error con 200) no toca la cola:
        //  se trata como un fallo del servidor, igual que no obtener respuesta.
        if (respuesta !== null && !Array.isArray((respuesta as { resultados?: unknown }).resultados)) respuesta = null;
        if (respuesta !== null) {
          resumen = await aplicarResultados(idPerfil, pendientesJson, respuesta.resultados);
          fallosDeRedSeguidos = 0;
        } else {
          fallosDeRedSeguidos += 1;
        }
      }

      //  SIEMPRE después del lote JSON — nunca antes: una evidencia sobre una
      //  NC creada EN ESTE MISMO lote (§4/§12) sólo puede resolver su
      //  dependencia una vez `aplicarResultados` ya escribió `resultadoNc`.
      if (pendientesEvidencia.length > 0) {
        if (!identidadSigueVigente(identidad, idPerfil)) return resumen;
        const resumenPropias = await sincronizarTransportePropio(idPerfil, pendientesEvidencia);
        resumen = combinarResumenes(resumen, resumenPropias);
      }

      if (resumen !== null) anotarUltimaSincronizacion(idPerfil, resumen);
      return resumen;
    } finally {
      fijarEstadoGlobal('INACTIVO');
    }
  } catch (e) {
    if (e instanceof ErrorPerfilBloqueado) return null;
    throw e;
  }
}

function combinarResumenes(a: ResumenSincronizacion | null, b: ResumenSincronizacion): ResumenSincronizacion {
  if (a === null) return b;
  return {
    intentadas: a.intentadas + b.intentadas,
    aplicadas: a.aplicadas + b.aplicadas,
    yaAplicadas: a.yaAplicadas + b.yaAplicadas,
    conConflicto: a.conConflicto + b.conConflicto,
    rechazadas: a.rechazadas + b.rechazadas,
    errorTemporal: a.errorTemporal + b.errorTemporal,
    omitidas: a.omitidas + b.omitidas,
  };
}

async function aplicarResultados(
  idPerfil: string,
  enviadas: readonly Mutacion[],
  resultados: readonly ResultadoItemSyncHttp[],
): Promise<ResumenSincronizacion> {
  const resumen = {
    intentadas: enviadas.length,
    aplicadas: 0,
    yaAplicadas: 0,
    conConflicto: 0,
    rechazadas: 0,
    errorTemporal: 0,
    omitidas: 0,
  };
  let huboAplicada = false;
  const porId = new Map(enviadas.map((m) => [m.idOperacionCliente, m] as const));

  for (const r of resultados) {
    switch (r.resultado) {
      case 'APLICADA':
      case 'YA_APLICADA': {
        //  F14.1 — cada dominio sabe reconciliar su «aplicada» (línea: estado y revisión; NC: id real y su
        //  revisión). El motor sólo la marca SINCRONIZADA y delega en el registro.
        //  Los datos propios de la operación (`datosAplicada`) van en la MISMA escritura que SINCRONIZADA.
        const m = porId.get(r.idOperacionCliente);
        const def = m === undefined ? undefined : definicionDe(m.tipo);
        const datos = m !== undefined && def?.datosAplicada !== undefined ? def.datosAplicada(m, r as unknown as ResultadoAplicadoLote) : {};
        await resolverMutacionTrasSincronizacion(idPerfil, r.idOperacionCliente, { estadoSync: 'SINCRONIZADA', ...datos });
        if (m !== undefined && def?.reconciliarAplicada !== undefined) {
          await def.reconciliarAplicada(idPerfil, m, r as unknown as ResultadoAplicadoLote);
        }
        huboAplicada = true;
        if (r.resultado === 'APLICADA') resumen.aplicadas += 1;
        else resumen.yaAplicadas += 1;
        break;
      }
      case 'CONFLICTO':
        await resolverMutacionTrasSincronizacion(idPerfil, r.idOperacionCliente, {
          estadoSync: 'EN_ESPERA',
          ultimoError: { codigo: r.codigo, mensaje: r.motivo },
          detalleConflicto: { revisionServidorActual: r.revisionServidorActual, estadoServidorActual: r.estadoServidorActual.estado },
          //  FASE 5.5 §2/§16 — esta SÍ es la revisión que cambió mientras el
          //  dispositivo estaba sin conexión, nunca una dependencia local
          //  sin resolver: distinción explícita para que la bandeja de
          //  revisión nunca mezcle los dos motivos (encargo §2).
          razonEspera: 'CONFLICTO_REVISION',
        });
        resumen.conConflicto += 1;
        break;
      case 'RECHAZADA':
        await resolverMutacionTrasSincronizacion(idPerfil, r.idOperacionCliente, {
          estadoSync: 'RECHAZADA',
          ultimoError: { codigo: r.codigo, mensaje: r.mensaje },
        });
        resumen.rechazadas += 1;
        break;
      case 'ERROR_REINTENTABLE':
        await resolverMutacionTrasSincronizacion(idPerfil, r.idOperacionCliente, {
          estadoSync: 'ERROR_REINTENTABLE',
          ultimoError: { codigo: r.codigo, mensaje: r.mensaje },
          incrementarIntentos: true,
        });
        resumen.errorTemporal += 1;
        break;
      case 'OMITIDA':
        await resolverMutacionTrasSincronizacion(idPerfil, r.idOperacionCliente, {
          estadoSync: 'EN_ESPERA',
          ultimoError: { codigo: r.codigo, mensaje: r.motivo },
          //  FASE 5.5 §2/§16 — la mutación de la que dependía nunca se
          //  aplicó: nunca hay una revisión de servidor con la que
          //  comparar (la entidad puede ni existir todavía), así que nunca
          //  se rellena `detalleConflicto` aquí — el bloqueo se resuelve
          //  siempre a través de la mutación PADRE (§13/§14).
          razonEspera: 'DEPENDENCIA_BLOQUEADA',
        });
        resumen.omitidas += 1;
        break;
    }
  }

  if (huboAplicada) {
    //  §19 — "cuando sea razonable, refrescar el paquete". Mejor
    //  esfuerzo: la reconciliación directa de arriba ya dejó el estado
    //  correcto aunque este refresco falle (sin conexión otra vez, por
    //  ejemplo).
    try {
      await prepararPaqueteOffline(idPerfil);
    } catch {
      /* la reconciliación directa ya es suficiente — ver comentario arriba */
    }
  }

  return resumen;
}

// ==================================================== transporte propio (F14.1; evidencias desde FASE 5.4)

/** Cuántos envíos propios en vuelo a la vez — fotos grandes en conexiones móviles: nunca todas a la vez. */
const CONCURRENCIA_PROPIAS = 2;

/**
 * Operaciones con transporte propio (hoy, evidencias): cada una con su `sincronizar` del registro, con un límite de
 * concurrencia. Se llama SIEMPRE después del lote JSON de la misma ronda: una evidencia sobre una NC creada en ese
 * lote sólo puede resolver su dependencia cuando el lote ya escribió `resultadoNc`.
 */
async function sincronizarTransportePropio(idPerfil: string, pendientes: readonly Mutacion[]): Promise<ResumenSincronizacion> {
  const resumen: ResumenParcial & { conConflicto: number } = { aplicadas: 0, yaAplicadas: 0, conConflicto: 0, rechazadas: 0, errorTemporal: 0, omitidas: 0 };
  for (let i = 0; i < pendientes.length; i += CONCURRENCIA_PROPIAS) {
    const lote = pendientes.slice(i, i + CONCURRENCIA_PROPIAS);
    await Promise.all(lote.map((m) => definicionDe(m.tipo)?.sincronizar?.(idPerfil, m, resumen) ?? Promise.resolve()));
  }
  return { intentadas: pendientes.length, ...resumen };
}

// -------------------------------------------------------- disparo automático (§16)

/**
 * Activa la sincronización automática para esta pestaña: al recuperar
 * conexión, y al volver a primer plano estando ya online. Devuelve una
 * función para desactivar los listeners (limpieza de efecto de React).
 * Nunca se llama sola dos veces sin desactivar la anterior — cada
 * componente que la usa la activa en su propio `useEffect`.
 */
export function activarSincronizacionAutomatica(idPerfil: string): () => void {
  let temporizador: ReturnType<typeof setTimeout> | null = null;
  let activo = true;

  function programar(): void {
    if (temporizador !== null) clearTimeout(temporizador);
    temporizador = null;
    if (!activo) return;
    //  F14.1 — reintento automático con espera creciente: tras un fallo de red, 30 s, 1 min, 2 min… (máx. 15 min);
    //  si no, al siguiente vencimiento de un error temporal. Nunca un bucle: un solo temporizador, y sólo con la
    //  pestaña visible.
    void (async () => {
      //  F14.2 — sin identidad (la sesión es de otro usuario, o se cerró) no se programa ningún reintento.
      if (!(await comprobarIdentidadSync(idPerfil)).ok) return;
      let espera: number | null = fallosDeRedSeguidos > 0 ? esperaReintento(fallosDeRedSeguidos) : null;
      try {
        const ahora = Date.now();
        for (const m of await listarMutacionesSincronizables(idPerfil)) {
          if (m.estadoSync !== 'ERROR_REINTENTABLE' || sincronizableAutomaticamente(m, ahora)) continue;
          const ultimo = m.ultimoIntentoEn == null ? ahora : Date.parse(m.ultimoIntentoEn);
          const vence = ultimo + esperaReintento(m.intentos) - ahora;
          if (m.intentos < MAX_REINTENTOS_AUTOMATICOS && (espera === null || vence < espera)) espera = Math.max(1_000, vence);
        }
      } catch {
        /* perfil bloqueado entretanto: no se programa nada */
      }
      if (espera === null || !activo) return;
      temporizador = setTimeout(() => {
        temporizador = null;
        if (document.visibilityState === 'visible') disparar();
        else programar();
      }, espera);
    })();
  }

  function disparar(): void {
    void sincronizarPendientes(idPerfil).finally(programar);
  }
  function alVisibilidad(): void {
    if (document.visibilityState === 'visible' && navigator.onLine) disparar();
  }

  window.addEventListener('online', disparar);
  document.addEventListener('visibilitychange', alVisibilidad);
  //  F14.1 — también cuando la comprobación REAL del servidor vuelve a ONLINE (una wifi sin salida no dispara
  //  `online`, y recuperar el servidor tampoco).
  window.addEventListener('neatos:conexion-recuperada', disparar);
  //  Reapertura de la aplicación ya online, con cola pendiente de una
  //  sesión anterior — sin esto, haría falta desconectar/reconectar a
  //  mano para que arrancara.
  if (navigator.onLine) disparar();

  return () => {
    activo = false;
    if (temporizador !== null) clearTimeout(temporizador);
    window.removeEventListener('online', disparar);
    document.removeEventListener('visibilitychange', alVisibilidad);
    window.removeEventListener('neatos:conexion-recuperada', disparar);
  };
}
